import React from 'react';
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";
import Nav from "./components/molecules/Nav";
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from "react-router-dom";
import Locations from "./pages/Locations";
import Analytics from "./pages/Analytics/Analytics";
import Ballpark from "./pages/Ballpark/Ballpark";
import RetailLocation from "./pages/Retail/RetailLocation";
import MobileRetailMap from "./pages/Retail/RetailMobileMap";
import Account from "./pages/Account";
import { AuthContextProvider } from "./contexts/auth-context";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import { useEffect, useContext } from "react";
import ReactGA from "react-ga";
import ConcessionLocation from "./pages/Concession/ConcessionLocation";
import AuthContext from "./contexts/auth-context";
import Changes from "./changes.json";
import semver from 'semver';
import moment from 'moment';
import RetailMobileMap from './pages/Retail/RetailMobileMap';
import FacilitiesLocation from './pages/Facilities/FacilitiesLocation';
import { USE_TEST_MODE } from './testMode';
import GatesLocation from './pages/Gates/GatesLocation';
// Fan View (Titans only): see its route below
import TitansWebviewDemo from './pages/webviews/nfl/titans/TitansWebviewDemo';

// Global JSON parse error handler that forces logout
const originalJSONParse = JSON.parse;
JSON.parse = function(text) {
  try {
    return originalJSONParse(text);
  } catch (error) {
    console.error("JSON parse error, forcing logout:", error);
    
    // Clear all auth data from localStorage
    localStorage.clear();
    
    // Only redirect if we're not already on the auth page
    if (window.location.pathname !== "/auth") {
      window.location.href = "/auth";
    }
    
    return {};
  }
};

// Init google tracking
ReactGA.initialize("G-RL7WP14SN0");

const usePageTracking = () => {
 const location = useLocation();
 useEffect(() => {
   ReactGA.set({ page: location.pathname });
   ReactGA.pageview(location.pathname + location.search);
 }, [location]);
};

const PageTracker = () => {
 usePageTracking();
 return null;
};


// Checks and compares the users localstorage dash version against minimum required version
// If version is less than minimum required --> logout
const VersionCheck = () => {
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  useEffect(() => {
    try {
      // Skip check if user is not logged in
      if (!authCtx.loggedIn) return;
      
      const latestVersion = Changes[0].versionName;
      // Hardcoded minimum required version - update this when needed
      const minRequiredVersion = "1.4.0"; // Set your minimum required version here
      
      // Get current user version from context
      const userVersion = authCtx.dashboardVersion || "0.0.0";
      // Check if user version is below minimum required
      if (semver.lt(userVersion, minRequiredVersion)) {
        // console.log(`Version Check: User version ${userVersion} is below minimum required ${minRequiredVersion}`);
        authCtx.logout();
        navigate('/auth', { replace: true });
      } 
      // Optionally check if user is not on latest version (but above minimum)
      else if (semver.lt(userVersion, latestVersion)) {
        // console.log(`Version Check: User can update from ${userVersion} to ${latestVersion}`);
        // Optional: Show update notification
        // authCtx.setUpdateAvailable(true);
      } else {
        // console.log('Version Check: User passed all checks')
      }
    } catch (error) {
      console.error("Version Check Error:", error);
      // If version check fails for any reason, force logout as a safety measure
      authCtx.logout();
      navigate('/auth', { replace: true });
    }
  }, [authCtx, navigate]);

  return null;
};

// This listens and checks for any errors
// If we get any error --> redirect to auth login
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  async componentDidCatch(error, errorInfo) {
    const { authContext, navigate } = this.props;
    if (authContext) {
      // We must await or redirecting state hangs
      await authContext.logout();
      this.setState({ hasError: false }, () => {
        navigate('/auth', { replace: true });
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return <div>Redirecting...</div>;
    }
    return this.props.children;
  }
}

const MLBRoute = ({ children }) => {
  const authCtx = useContext(AuthContext);
  if (authCtx.league !== 'mlb') {
    return <Navigate to="/locations" replace />;
  }
  return children;
};

// Titans users only: not logged in or session expired -> /auth, any other venue -> /locations
const TitansRoute = ({ children }) => {
  const authCtx = useContext(AuthContext);
  const isSessionValid = authCtx.loggedIn && moment.unix(authCtx.tokenExpirationTime) > moment();
  if (!isSessionValid) {
    return <Navigate to="/auth" replace />;
  }
  if (authCtx.venue !== 'titansNFL') {
    return <Navigate to="/locations" replace />;
  }
  return children;
};

function NavigationControl() {
 const navigate = useNavigate();
 const authCtx = useContext(AuthContext);

 return (
   <ErrorBoundary authContext={authCtx} navigate={navigate}>
     <PageTracker />
     <VersionCheck />
     {/* TEMPORARY: remove with src/testMode.js */}
     {USE_TEST_MODE && (
       <div
         role="status"
         style={{
           position: 'fixed', right: 16, bottom: 16, zIndex: 10001, padding: '6px 14px', borderRadius: 999,
           background: '#d97706', color: '#fff', fontWeight: 600, fontSize: 13, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
           pointerEvents: 'none',
         }}
       >
         Test mode: dummy live game, all data is fake (local dev only)
       </div>
     )}
     <Routes>
       <Route path="/" element={<Nav />}>
         <Route path="/auth" element={<Login />} />
         <Route path="/auth/forgotPassword" element={<ForgotPassword />} />
         <Route path="/locations" element={<Locations />} />
         <Route path="/locations/retail/:name" element={<RetailLocation />} />
         <Route path="/locations/retail/:name/map" element={<RetailMobileMap />} />
         <Route path="/locations/concession/:name" element={<ConcessionLocation />} />
         <Route path="/locations/facilities/:name" element={<FacilitiesLocation />} />
         <Route path="/locations/gates" element={<GatesLocation />} />
         <Route path="/ballpark" element={<MLBRoute><Ballpark /></MLBRoute>} />
         <Route path="/analytics" element={<Analytics />} />
         <Route path="/account" element={<Account />} />
         {/* Fan View (Titans webview demo): Titans users only (TitansRoute: logged in with a valid session
             and venue titansNFL), shown in the sidebar under Analytics for Titans users (Nav.js). To remove
             it, delete this route, its import above and the "fanview" link in Nav.js. */}
         <Route path="/fanview" element={<TitansRoute><TitansWebviewDemo /></TitansRoute>} />
       </Route>
     </Routes>
   </ErrorBoundary>
 );
}

function App() {
 return (
   <AuthContextProvider>
     <BrowserRouter>
       <NavigationControl />
     </BrowserRouter>
   </AuthContextProvider>
 );
}

export default App;